import { useState } from "react";
import { Link } from "react-router-dom";
import { useBudget, type BudgetLine, type BudgetStatus } from "@/api/cash";
import { SPEND_PURPOSES, labelOf } from "@/api/finance";
import { RequestError } from "@/components/RequestError";
import { Alert } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { TextField } from "@/components/ui/field";
import { formatInr } from "@/lib/format";

const TH = "px-4 py-2 font-medium";
const THEAD =
  "border-b bg-muted/50 text-left text-xs uppercase tracking-wide text-muted-foreground";

const STATUS: Record<
  BudgetStatus,
  { label: string; tone: "destructive" | "warning" | "success" | "muted" }
> = {
  over: { label: "Over limit", tone: "destructive" },
  fund_short: { label: "Fund short", tone: "destructive" },
  near: { label: "Near limit", tone: "warning" },
  within: { label: "Within limit", tone: "success" },
  no_limit: { label: "No limit", tone: "muted" },
};

function money(value: string | null): string {
  return value === null ? "—" : formatInr(value);
}

function tone(line: BudgetLine): string {
  return line.over || Number(line.fund_inr) < 0 ? "text-destructive" : "";
}

export function BudgetPanel() {
  const [month, setMonth] = useState("");
  const budget = useBudget(month || undefined);
  if (budget.isPending) return <p className="text-muted-foreground">Loading budget…</p>;
  if (budget.isError) return <RequestError error={budget.error} />;
  const report = budget.data;
  const purposes = report.by_purpose;
  const purposeTotal = purposes.reduce((sum, row) => sum + Number(row.fy_inr), 0);
  return (
    <div className="flex flex-col gap-6">
      <div className="grid gap-3 sm:grid-cols-[12rem_1fr] sm:items-end">
        <TextField
          id="budget-month"
          label="Month"
          type="month"
          value={month || report.month}
          onChange={setMonth}
        />
        <p className="text-sm text-muted-foreground">
          {report.planned_income_inr
            ? `Planned income ${formatInr(report.planned_income_inr)} a month; each bucket's ceiling is its share unless it sets its own.`
            : "No planned monthly income is set, so only buckets with their own limit have a ceiling."}{" "}
          <Link to="/settings?tab=company" className="text-primary hover:underline">
            Set it in Settings
          </Link>
          .
        </p>
      </div>
      {report.over.length > 0 && (
        <Alert
          variant="warning"
          title={`Over budget in ${report.label}: ${report.over.join(", ")}`}
        />
      )}
      {report.fund_short.length > 0 && (
        <Alert
          variant="warning"
          title={`Spending ahead of what clients have paid: ${report.fund_short.join(", ")}`}
        >
          The fund is the bucket's share of everything received so far, less everything spent
          from it so far. A negative fund means the money has gone out before it came in.
        </Alert>
      )}

      <section aria-labelledby="budget-month-heading" className="flex flex-col gap-2">
        <h3 id="budget-month-heading" className="text-sm font-semibold text-muted-foreground">
          {report.label}: each bucket against its monthly limit, and its fund
        </h3>
        <div className="table-frame">
          <table className="data-table">
            <thead className={THEAD}>
              <tr>
                <th className={TH}>Bucket</th>
                <th className={`${TH} text-right`}>Share</th>
                <th className={`${TH} text-right`}>Ceiling</th>
                <th className={`${TH} text-right`}>Spent</th>
                <th className={`${TH} text-right`}>Left</th>
                <th className={`${TH} text-right`}>Fund</th>
                <th className={TH}>Status</th>
              </tr>
            </thead>
            <tbody>
              {report.lines.map((row) => (
                <tr key={row.bucket_id} className="border-b last:border-0">
                  <td className="px-4 py-2">{row.name}</td>
                  <td className="px-4 py-2 text-right tabular-nums">{Number(row.share_percent)}%</td>
                  <td className="px-4 py-2 text-right tabular-nums">{money(row.limit_inr)}</td>
                  <td className="px-4 py-2 text-right tabular-nums">{formatInr(row.spent_inr)}</td>
                  <td className={`px-4 py-2 text-right tabular-nums ${row.over ? "text-destructive" : ""}`}>
                    {money(row.left_inr)}
                  </td>
                  <td className={`px-4 py-2 text-right tabular-nums ${tone(row)}`}>
                    {formatInr(row.fund_inr)}
                  </td>
                  <td className="px-4 py-2">
                    <Badge variant={STATUS[row.status].tone}>{STATUS[row.status].label}</Badge>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <p className="text-xs text-muted-foreground">
          Spent counts every bill dated in the month, paid or not: the money is committed. Near
          limit means four fifths of the ceiling is used.
        </p>
      </section>

      <section aria-labelledby="budget-grid-heading" className="flex flex-col gap-2">
        <h3 id="budget-grid-heading" className="text-sm font-semibold text-muted-foreground">
          Spent each month of the financial year (INR)
        </h3>
        <div className="table-frame">
          <table className="data-table">
            <thead className={THEAD}>
              <tr>
                <th className={TH}>Bucket</th>
                <th className={`${TH} text-right`}>Limit</th>
                {report.grid_months.map((cell) => (
                  <th key={cell.month} className={`${TH} text-right`}>
                    {cell.label}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {report.grid.map((row) => (
                <tr key={row.bucket_id} className="border-b last:border-0">
                  <td className="whitespace-nowrap px-4 py-2">{row.name}</td>
                  <td className="whitespace-nowrap px-4 py-2 text-right tabular-nums">
                    {money(row.limit_inr)}
                  </td>
                  {row.months.map((spent, index) => {
                    const over =
                      spent !== null && row.limit_inr !== null && Number(spent) > Number(row.limit_inr);
                    const near =
                      !over &&
                      spent !== null &&
                      row.limit_inr !== null &&
                      Number(row.limit_inr) > 0 &&
                      Number(spent) >= Number(row.limit_inr) * 0.8;
                    return (
                      <td
                        key={report.grid_months[index]?.month ?? index}
                        className={`whitespace-nowrap px-4 py-2 text-right tabular-nums ${
                          over ? "text-destructive" : near ? "text-warning-foreground" : ""
                        }`}
                      >
                        {spent === null ? "" : Number(spent) === 0 ? "-" : formatInr(spent)}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      <section aria-labelledby="budget-purpose-heading" className="flex flex-col gap-2">
        <h3 id="budget-purpose-heading" className="text-sm font-semibold text-muted-foreground">
          What the money went on
        </h3>
        {purposes.length === 0 ? (
          <p className="text-sm text-muted-foreground">Nothing spent this financial year yet.</p>
        ) : (
          <div className="table-frame">
            <table className="data-table">
              <thead className={THEAD}>
                <tr>
                  <th className={TH}>Purpose</th>
                  <th className={`${TH} text-right`}>{report.label}</th>
                  <th className={`${TH} text-right`}>FY to date</th>
                  <th className={`${TH} text-right`}>Entries</th>
                  <th className={`${TH} text-right`}>Share</th>
                </tr>
              </thead>
              <tbody>
                {purposes.map((row) => (
                  <tr key={row.purpose ?? "none"} className="border-b last:border-0">
                    <td className="px-4 py-2">
                      {row.purpose ? labelOf(SPEND_PURPOSES, row.purpose) : "Purpose not given"}
                    </td>
                    <td className="px-4 py-2 text-right tabular-nums">{formatInr(row.month_inr)}</td>
                    <td className="px-4 py-2 text-right tabular-nums">{formatInr(row.fy_inr)}</td>
                    <td className="px-4 py-2 text-right tabular-nums">{row.entries}</td>
                    <td className="px-4 py-2 text-right tabular-nums">
                      {purposeTotal > 0 ? `${Math.round((Number(row.fy_inr) / purposeTotal) * 100)}%` : "—"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </div>
  );
}
